<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { Search, Plus, ChatLineRound } from '@element-plus/icons-vue'
import { useChatStore } from '/src/stores/chat'
import { useAuthStore } from '/src/stores/auth'

const chatStore = useChatStore()
const authStore = useAuthStore()

const searchQuery = ref('')
const showSessionMessages = ref(false)
const selectedSession = ref(null)
const messageInput = ref('')

const filteredPinnedSessions = computed(() => {
  if (!searchQuery.value) return chatStore.pinnedSessions
  const query = searchQuery.value.toLowerCase()
  return chatStore.pinnedSessions.filter(s =>
    s.title?.toLowerCase().includes(query) ||
    s.lastMessage?.toLowerCase().includes(query)
  )
})

const filteredRecentSessions = computed(() => {
  if (!searchQuery.value) return chatStore.recentSessions
  const query = searchQuery.value.toLowerCase()
  return chatStore.recentSessions.filter(s =>
    s.title?.toLowerCase().includes(query) ||
    s.lastMessage?.toLowerCase().includes(query)
  )
})

const formatTime = (date) => {
  if (!date) return ''
  const now = new Date()
  const diff = now - new Date(date)
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 2) return '昨天'
  if (days < 7) return `${days}天前`

  return new Date(date).toLocaleDateString('zh-CN', {
    month: 'short',
    day: 'numeric'
  })
}

const handleSessionClick = async (session) => {
  selectedSession.value = session
  showSessionMessages.value = true
  chatStore.clearUnread(session.id)
  await chatStore.fetchMessages(session.id)
}

const handleBack = () => {
  showSessionMessages.value = false
  selectedSession.value = null
  messageInput.value = ''
}

const handleSendMessage = async () => {
  if (!messageInput.value.trim() || !selectedSession.value) return

  try {
    await chatStore.sendMessage(selectedSession.value.id, messageInput.value)
    messageInput.value = ''
  } catch (err) {
    console.error('发送消息失败:', err)
  }
}

const handleCreateSession = () => {
  console.log('创建新会话')
}

onMounted(() => {
  chatStore.fetchSessions()
})

watch(() => chatStore.activeTab, (tab) => {
  if (tab === 'chats') {
    chatStore.fetchSessions()
  }
})
</script>

<template>
  <div class="chats-view">
    <template v-if="!showSessionMessages">
      <div class="search-bar">
        <el-input
          v-model="searchQuery"
          placeholder="搜索会话..."
          :prefix-icon="Search"
          clearable
          size="small"
        />
      </div>

      <div class="sessions-list">
        <template v-if="filteredPinnedSessions.length > 0">
          <div class="section-title">
            <el-icon><Pin /></el-icon>
            <span>置顶会话</span>
          </div>
          <div
            v-for="session in filteredPinnedSessions"
            :key="session.id"
            class="session-item"
            @click="handleSessionClick(session)"
          >
            <div class="session-avatar">
              <el-icon :size="24"><ChatLineRound /></el-icon>
            </div>
            <div class="session-content">
              <div class="session-header">
                <span class="session-title">{{ session.title || '会话' }}</span>
                <span class="session-time">{{ formatTime(session.lastActivity) }}</span>
              </div>
              <div class="session-preview">
                {{ session.lastMessage || '暂无消息' }}
              </div>
              <div class="session-tags" v-if="session.taskId">
                <el-tag size="small" type="info">关联任务</el-tag>
              </div>
            </div>
            <div v-if="chatStore.unreadBySession[session.id]" class="unread-badge">
              {{ chatStore.unreadBySession[session.id] }}
            </div>
          </div>
        </template>

        <template v-if="filteredRecentSessions.length > 0">
          <div class="section-title">
            <span>所有会话</span>
          </div>
          <div
            v-for="session in filteredRecentSessions"
            :key="session.id"
            class="session-item"
            @click="handleSessionClick(session)"
          >
            <div class="session-avatar">
              <el-icon :size="24"><ChatLineRound /></el-icon>
            </div>
            <div class="session-content">
              <div class="session-header">
                <span class="session-title">{{ session.title || '会话' }}</span>
                <span class="session-time">{{ formatTime(session.lastActivity) }}</span>
              </div>
              <div class="session-preview">
                {{ session.lastMessage || '暂无消息' }}
              </div>
              <div class="session-meta">
                <span v-if="session.expiresAt">剩余: {{ formatTime(session.expiresAt) }}</span>
              </div>
            </div>
            <div v-if="chatStore.unreadBySession[session.id]" class="unread-badge">
              {{ chatStore.unreadBySession[session.id] }}
            </div>
          </div>
        </template>

        <div v-if="filteredPinnedSessions.length === 0 && filteredRecentSessions.length === 0" class="empty-state">
          <el-icon :size="48" color="#d1d5db"><ChatLineRound /></el-icon>
          <p>暂无会话</p>
          <el-button type="primary" size="small" @click="handleCreateSession">
            <el-icon><Plus /></el-icon>
            创建会话
          </el-button>
        </div>
      </div>

      <div class="bottom-action">
        <el-button type="primary" class="create-btn" @click="handleCreateSession">
          <el-icon><Plus /></el-icon>
          新建会话
        </el-button>
      </div>
    </template>

    <template v-else>
      <div class="message-view">
        <div class="message-header">
          <el-button text @click="handleBack">
            <el-icon><ArrowLeft /></el-icon>
          </el-button>
          <span class="message-title">{{ selectedSession?.title }}</span>
        </div>

        <div class="messages-list">
          <div
            v-for="msg in chatStore.messages"
            :key="msg.id"
            class="message-item"
            :class="{ 'is-mine': msg.senderId === authStore.user?.id }"
          >
            <div class="message-avatar">
              <el-avatar :size="32">{{ msg.senderName?.[0] || '?' }}</el-avatar>
            </div>
            <div class="message-bubble">
              <div class="message-sender">{{ msg.senderName }}</div>
              <div class="message-content">{{ msg.content }}</div>
              <div class="message-time">{{ formatTime(msg.timestamp) }}</div>
            </div>
          </div>

          <div v-if="chatStore.messages.length === 0" class="empty-messages">
            暂无消息，开始聊天吧
          </div>
        </div>

        <div class="message-input-area">
          <el-input
            v-model="messageInput"
            placeholder="输入消息..."
            @keyup.enter="handleSendMessage"
          />
          <el-button type="primary" :disabled="!messageInput.trim()" @click="handleSendMessage">
            发送
          </el-button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.chats-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-bar {
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.sessions-list {
  flex: 1;
  overflow-y: auto;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
}

.session-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.session-item:hover {
  background: #f3f4f6;
}

.session-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: #e0f2fe;
  color: #0ea5e9;
  border-radius: 10px;
}

.session-content {
  flex: 1;
  min-width: 0;
}

.session-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.session-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2937;
}

.session-time {
  font-size: 12px;
  color: #9ca3af;
}

.session-preview {
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.session-meta {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

.session-tags {
  margin-top: 4px;
}

.unread-badge {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #ef4444;
  color: white;
  font-size: 11px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
  border-radius: 10px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.empty-state p {
  margin: 12px 0;
}

.bottom-action {
  padding: 12px;
  border-top: 1px solid #e5e7eb;
}

.create-btn {
  width: 100%;
}

.message-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.message-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #e5e7eb;
}

.message-title {
  font-size: 14px;
  font-weight: 500;
}

.messages-list {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
}

.message-item {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.message-item.is-mine {
  flex-direction: row-reverse;
}

.message-item.is-mine .message-bubble {
  background: #0ea5e9;
  color: white;
  border-radius: 12px 12px 0 12px;
}

.message-bubble {
  max-width: 70%;
  padding: 8px 12px;
  background: white;
  border-radius: 12px 12px 12px 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.message-sender {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 4px;
}

.message-item.is-mine .message-sender {
  display: none;
}

.message-content {
  font-size: 14px;
  line-height: 1.5;
}

.message-time {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}

.message-item.is-mine .message-time {
  color: rgba(255, 255, 255, 0.7);
}

.empty-messages {
  text-align: center;
  color: #9ca3af;
  padding: 40px;
}

.message-input-area {
  display: flex;
  gap: 8px;
  padding: 12px;
  border-top: 1px solid #e5e7eb;
}
</style>
